import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Avatar, 
  Rating 
} from "@mui/material";

const TestimonialsComponent = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate geometric particles
    const newParticles = [];
    for (let i = 0; i < 20; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 15 + 5,
        duration: Math.random() * 15 + 10,
        delay: Math.random() * 8,
        type: Math.random() > 0.5 ? 'circle' : 'square',
        opacity: Math.random() * 0.3 + 0.1,
      });
    }
    setParticles(newParticles);
  }, []);

  const testimonials = [
    {
      id: 1,
      role: " Head of HR",
      company: "SaaS Company",
      rating: 5,
      text: "Our remote team LOVED it. They were laughing, chatting, and suddenly more connected",
      avatar: "HR",
    },
    {
      id: 2,
      role: "Event Organizer",
      company: "Leadership Forum 2024",
      rating: 5,
      text: "Perfect energizer between sessions at our summit",
      avatar: "MC",
    },
  ];

  const keyframes = `
    @keyframes geometricFloat {
      0%, 100% {
        transform: translateY(0px) rotate(0deg) scale(1);
      }
      33% {
        transform: translateY(-15px) rotate(120deg) scale(1.1);
      }
      66% {
        transform: translateY(10px) rotate(240deg) scale(0.9);
      }
    }
    
    @keyframes slowPulse {
      0%, 100% {
        transform: scale(1);
        opacity: 0.8;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.4;
      }
    }
    
    @keyframes streak {
      0% {
        opacity: 0;
        transform: translateY(-50px) rotate(45deg);
      }
      50% {
        opacity: 1;
        transform: translateY(0px) rotate(45deg);
      }
      100% {
        opacity: 0;
        transform: translateY(50px) rotate(45deg);
      }
    }
    
    @keyframes dotFloat {
      0%, 100% {
        transform: translateY(0px) scale(1);
      }
      50% {
        transform: translateY(-25px) scale(1.3);
      }
    }
    
    @keyframes hexagonSpin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  `;

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        py: 10,
        minHeight: 'auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{keyframes}</style>
      
      {/* Animated Geometric Particles */}
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            zIndex: 10,
            borderRadius: particle.type === 'circle' ? '50%' : '4px',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: particle.type === 'circle' 
              ? 'linear-gradient(45deg, rgba(138, 43, 226, 0.6), rgba(0, 255, 255, 0.4))'
              : 'linear-gradient(45deg, rgba(255, 107, 107, 0.5), rgba(78, 205, 196, 0.4))',
            animation: `geometricFloat ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
            opacity: particle.opacity,
            boxShadow: '0 0 20px rgba(138, 43, 226, 0.3)',
          }}
        />
      ))}

      {/* Static Grid Lines */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          backgroundImage: `
            linear-gradient(rgba(138, 43, 226, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(138, 43, 226, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
        }}
      />

      {/* Animated Gradient Orbs */}
      <Box 
        sx={{
          position: 'absolute',
          width: '384px',
          height: '384px',
          borderRadius: '50%',
          zIndex: 10,
          top: '-10%',
          right: '-10%',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.15) 0%, rgba(138, 43, 226, 0.1) 40%, transparent 70%)',
          animation: "slowPulse 8s ease-in-out infinite",
        }}
      />

      <Box 
        sx={{
          position: 'absolute',
          width: '320px',
          height: '320px',
          borderRadius: '50%',
          zIndex: 10,
          bottom: '-5%',
          left: '-5%',
          background: 'radial-gradient(circle, rgba(255, 107, 107, 0.12) 0%, rgba(78, 205, 196, 0.08) 40%, transparent 70%)',
          animation: "slowPulse 10s ease-in-out infinite 2s",
        }}
      />

      {/* Diagonal Light Streaks */}
      <Box 
        sx={{
          position: 'absolute',
          width: '8px',
          height: '384px',
          zIndex: 10,
          top: '10%',
          left: '20%',
          background: 'linear-gradient(45deg, transparent, rgba(0, 255, 255, 0.2), transparent)',
          transform: 'rotate(45deg)',
          animation: "streak 12s ease-in-out infinite",
        }}
      />

      <Box 
        sx={{
          position: 'absolute',
          width: '4px',
          height: '320px',
          zIndex: 10,
          top: '60%',
          right: '25%',
          background: 'linear-gradient(45deg, transparent, rgba(255, 107, 107, 0.25), transparent)',
          transform: 'rotate(-45deg)',
          animation: "streak 15s ease-in-out infinite 3s",
        }}
      />

      {/* Floating Dots */}
      <Box 
        sx={{
          position: 'absolute',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          zIndex: 10,
          top: '25%',
          left: '15%',
          background: 'rgba(138, 43, 226, 0.6)',
          animation: "dotFloat 6s ease-in-out infinite",
          boxShadow: '0 0 15px rgba(138, 43, 226, 0.8)',
        }}
      />

      <Box 
        sx={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          zIndex: 10,
          top: '70%',
          right: '30%',
          background: 'rgba(0, 255, 255, 0.7)',
          animation: "dotFloat 8s ease-in-out infinite 2s",
          boxShadow: '0 0 12px rgba(0, 255, 255, 0.9)',
        }}
      />

      <Box 
        sx={{
          position: 'absolute',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          zIndex: 10,
          top: '45%',
          right: '10%',
          background: 'rgba(255, 107, 107, 0.5)',
          animation: "dotFloat 7s ease-in-out infinite 4s",
          boxShadow: '0 0 18px rgba(255, 107, 107, 0.7)',
        }}
      />

      {/* Hexagon Shapes */}
      <Box 
        sx={{
          position: 'absolute',
          width: '32px',
          height: '32px',
          zIndex: 10,
          top: '15%',
          right: '20%',
          background: 'rgba(78, 205, 196, 0.3)',
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          animation: "hexagonSpin 20s linear infinite",
        }}
      />

      <Box 
        sx={{
          position: 'absolute',
          width: '24px',
          height: '24px',
          zIndex: 10,
          bottom: '30%',
          left: '25%',
          background: 'rgba(138, 43, 226, 0.4)',
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          animation: "hexagonSpin 25s linear infinite reverse",
        }}
      />

      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 2, position: 'relative', zIndex: 20 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            component="h2"
            sx={{
              fontSize: { xs: '2rem', lg: '3rem' },
              fontWeight: 900,
              color: '#000000',
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            What Our Gamers Say
          </Typography>
          <Typography 
            variant="h5" 
            component="p"
            sx={{
              fontSize: '1.25rem',
              color: '#000000',
              fontWeight: 300,
            }}
          >
            Level up your team experience. Here's what our gaming community has
            to say about their epic adventures with us.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  zIndex: 20,
                  p: 3,
                  boxShadow: "0 4px 30px rgba(138, 43, 226, 0.3)",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(138, 43, 226, 0.2)",
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-8px)',
                    boxShadow: "0 8px 40px rgba(138, 43, 226, 0.4), 0 0 20px rgba(0, 255, 255, 0.3)",
                    border: "1px solid rgba(0, 255, 255, 0.5)",
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Typography 
                    variant="body1" 
                    component="p"
                    sx={{
                      fontSize: '1.125rem',
                      lineHeight: 1.6,
                      color: '#000000',
                      mb: 3,
                      fontStyle: 'italic',
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>
                </CardContent>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{
                      width: 60,
                      height: 60,
                      background: "linear-gradient(45deg, #ff6b6b, #4ecdc4)",
                      boxShadow: "0 0 20px rgba(255, 107, 107, 0.5)",
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {testimonial.avatar}
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography 
                      variant="h6" 
                      component="p"
                      sx={{
                        fontSize: '1.125rem',
                        fontWeight: 'bold',
                        color: '#000000',
                      }}
                    >
                      {testimonial.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="p"
                      sx={{
                        fontSize: '0.875rem',
                        color: '#000000',
                      }}
                    >
                      {testimonial.role}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="p"
                      sx={{
                        fontSize: '0.875rem',
                        color: '#000000',
                      }}
                    >
                      {testimonial.company}
                    </Typography>
                    <Rating 
                      value={testimonial.rating} 
                      readOnly 
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: '#22c55e',
                        },
                        '& .MuiRating-iconEmpty': {
                          color: 'rgba(34, 197, 94, 0.3)',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default TestimonialsComponent;